// src/pages/recruiter-dashboard/index.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderBar from 'components/ui/HeaderBar';
import RoleBasedSidebar from 'components/ui/RoleBasedSidebar';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import LoadingSpinner from 'components/ui/LoadingSpinner';
import Icon from 'components/AppIcon';
import { useAuth } from 'context/AuthContext';
import { getUserRoleForComponents } from 'utils/authHelpers';
import { useNotifications } from 'hooks/useNotifications';
import { recruiterAPI } from 'utils/api';
import Notifier from 'components/ui/Notifier';

const RecruiterDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const userRole = getUserRoleForComponents(user);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeltas, setShowDeltas] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    biodata_stats: {
      total: 0,
      drafts: 0,
      submitted: 0,
      approved: 0,
      rejected: 0,
      selected: 0,
      amended: 0,
      expiring_medicals_30d: 0,
      incomplete_drafts: 0
    },
    deltas: {},
    by_nationality: {},
    analytics: {
      approval_rate: 0,
      rejection_rate: 0,
      selection_rate: 0,
      avg_processing_time: 0
    }
  });

  // Notifications
  const { 
    notifications, 
    showSuccess, 
    showError, 
    removeNotification: dismissNotification 
  } = useNotifications();

  // Load live dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        // Call API with delta parameter only
        const params = new URLSearchParams();
        if (showDeltas) params.append('include_delta', 'true');
        
        const paramString = params.toString() ? '?' + params.toString() : '';
        const res = await recruiterAPI.getDashboard(paramString);
        
        if (res?.success && res?.data) {
          const d = res.data;
          const stats = d.stats || {};
          setDashboardData({
            biodata_stats: {
              total: stats.total || 0,
              drafts: stats.draft || 0,
              submitted: stats.pending || 0,
              approved: stats.approved || 0,
              rejected: stats.rejected || 0,
              selected: stats.selected || 0,
              amended: stats.resubmitted || 0,
              expiring_medicals_30d: stats.expiring_medicals_30d || 0,
              incomplete_drafts: stats.incomplete_drafts || 0
            },
            deltas: d.deltas || {},
            by_nationality: d.by_nationality || {},
            analytics: {
              approval_rate: stats.total ? Math.round(((stats.approved || 0) / stats.total) * 1000) / 10 : 0,
              rejection_rate: stats.total ? Math.round(((stats.rejected || 0) / stats.total) * 1000) / 10 : 0,
              selection_rate: stats.total ? Math.round(((stats.selected || 0) / stats.total) * 1000) / 10 : 0,
              avg_processing_time: 0
            }
          });
        } else {
          showError(res?.message || 'Failed to load dashboard');
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        showError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, [showError, showDeltas]);


  const quickActions = [
    {
      title: 'Create New Biodata',
      description: 'Register a new foreign worker',
      icon: 'Plus',
      color: 'bg-primary-500',
      action: () => navigate('/recruiter-biodata-management'),
      count: null
    },
    {
      title: 'Review Drafts',
      description: 'Complete pending biodata',
      icon: 'FileEdit',
      color: 'bg-warning-500',
      action: () => navigate('/recruiter-biodata-management?tab=drafts'),
      count: dashboardData.biodata_stats.drafts
    },
    {
      title: 'Expiring Medicals',
      description: 'Update medicals expiring in 30 days',
      icon: 'Calendar',
      color: 'bg-warning-500',
      action: () => navigate('/recruiter-biodata-management?filter=expiring_medical'),
      count: dashboardData.biodata_stats.expiring_medicals_30d
    },
    {
      title: 'Fix Incomplete Drafts',
      description: 'Complete missing information',
      icon: 'AlertTriangle',
      color: 'bg-error-500',
      action: () => navigate('/recruiter-biodata-management?filter=incomplete_draft'),
      count: dashboardData.biodata_stats.incomplete_drafts
    },
    {
      title: 'Resubmit Amended',
      description: 'Submit corrected biodata',
      icon: 'RefreshCw',
      color: 'bg-accent-500',
      action: () => navigate('/recruiter-biodata-management?tab=resubmitted'),
      count: dashboardData.biodata_stats.amended
    },
    {
      title: 'Upload Bulk Data',
      description: 'Import multiple biodata via Excel',
      icon: 'Upload',
      color: 'bg-accent-500',
      action: () => navigate('/recruiter-biodata-management?action=bulk-upload'),
      count: null
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderBar 
          userRole={userRole} 
          userName={userProfile?.full_name || 'Recruiter'}
          onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        
        <RoleBasedSidebar 
          userRole={userRole}
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <main className={`transition-all duration-300 pt-16 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <LoadingSpinner />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Global Notifier */}
      <Notifier
        type={notifications.length > 0 ? notifications[notifications.length - 1].type : 'info'}
        message={notifications.length > 0 ? notifications[notifications.length - 1].message : ''}
        show={notifications.length > 0}
        onClose={() => dismissNotification(notifications[notifications.length - 1]?.id)}
        duration={notifications.length > 0 ? notifications[notifications.length - 1].duration : 3000}
      />

      {/* Header */}
      <HeaderBar 
        userRole={userRole} 
        userName={userProfile?.full_name || 'Recruiter'}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      
      {/* Sidebar */}
      <RoleBasedSidebar 
        userRole={userRole}
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content */}
      <main className={`transition-all duration-300 pt-16 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-6">
          {/* Breadcrumb */}
          <BreadcrumbNavigation userRole={userRole} />
          
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">
                  Welcome back, {userProfile?.full_name || 'Recruiter'}
                </h1>
                <p className="text-text-secondary">
                  Manage your foreign worker biodata and track submission progress
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/recruiter-biodata-management')}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors duration-150"
                >
                  <Icon name="Plus" size={16} />
                  <span>New Biodata</span>
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="flex items-center space-x-2 px-4 py-2 bg-surface border border-secondary-300 text-text-secondary rounded-lg hover:bg-secondary-50 transition-colors duration-150"
                >
                  <Icon name="RefreshCw" size={16} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Total Biodata</p>
                  <p className="text-3xl font-bold text-text-primary">{dashboardData.biodata_stats.total}</p>
                  {showDeltas && dashboardData.deltas?.total > 0 && (
                    <p className="text-xs text-success mt-1">+{dashboardData.deltas.total} this week</p>
                  )}
                </div>
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={24} className="text-primary" />
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Approved</p>
                  <p className="text-3xl font-bold text-success">{dashboardData.biodata_stats.approved}</p>
                  {showDeltas && dashboardData.deltas?.approved > 0 && (
                    <p className="text-xs text-success mt-1">+{dashboardData.deltas.approved} this week</p>
                  )}
                </div>
                <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} className="text-success" />
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Pending Review</p>
                  <p className="text-3xl font-bold text-warning">{dashboardData.biodata_stats.submitted}</p>
                  {showDeltas && dashboardData.deltas?.pending > 0 && (
                    <p className="text-xs text-warning mt-1">+{dashboardData.deltas.pending} this week</p>
                  )}
                </div>
                <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={24} className="text-warning" />
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Rejected</p>
                  <p className="text-3xl font-bold text-error">{dashboardData.biodata_stats.rejected}</p>
                  {showDeltas && dashboardData.deltas?.rejected > 0 && (
                    <p className="text-xs text-error mt-1">+{dashboardData.deltas.rejected} this week</p>
                  )}
                </div>
                <div className="w-12 h-12 bg-error-100 rounded-lg flex items-center justify-center">
                  <Icon name="XCircle" size={24} className="text-error" />
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Expiring Medicals</p>
                  <p className="text-3xl font-bold text-warning">{dashboardData.biodata_stats.expiring_medicals_30d}</p>
                  <p className="text-xs text-text-muted mt-1">Next 30 days</p>
                </div>
                <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
                  <Icon name="Calendar" size={24} className="text-warning" />
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-secondary">Incomplete Drafts</p>
                  <p className="text-3xl font-bold text-error">{dashboardData.biodata_stats.incomplete_drafts}</p>
                  <p className="text-xs text-text-muted mt-1">Need completion</p>
                </div>
                <div className="w-12 h-12 bg-error-100 rounded-lg flex items-center justify-center">
                  <Icon name="FileEdit" size={24} className="text-error" />
                </div>
              </div>
            </div>
          </div>

          {/* Secondary Stats Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Top Nationalities */}
            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
                  <Icon name="Globe" size={20} className="text-primary" />
                  <span>Top Nationalities</span>
                </h3>
              </div>
              <div className="p-6">
                {Object.keys(dashboardData.by_nationality).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(dashboardData.by_nationality)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 5)
                      .map(([nationality, count]) => (
                      <div key={nationality} className="flex items-center justify-between">
                        <span className="text-sm text-text-primary font-medium">{nationality}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-20 bg-secondary-200 rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full" 
                              style={{ width: `${Math.min(100, (count / Math.max(...Object.values(dashboardData.by_nationality))) * 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-primary w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-4xl mb-2">🌍</div>
                    <p className="text-text-secondary text-sm">No biodata records yet</p>
                    <p className="text-text-muted text-xs mt-1">Data will appear when you add workers</p>
                  </div>
                )}
              </div>
            </div>

            {/* Dashboard Controls */}
            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
                  <Icon name="Settings" size={20} className="text-accent" />
                  <span>Dashboard Controls</span>
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={showDeltas}
                      onChange={(e) => setShowDeltas(e.target.checked)}
                      className="rounded border-secondary-300 text-primary focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-text-secondary">Show weekly trends</span>
                  </label>
                </div>

                <div className="pt-4 border-t border-secondary-200">
                  <p className="text-xs text-text-muted">Last updated: {new Date().toLocaleTimeString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions & Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Quick Actions */}
            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
                  <Icon name="Zap" size={20} className="text-primary" />
                  <span>Quick Actions</span>
                </h3>
              </div>
              <div className="p-6 space-y-4">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={action.action}
                    className="w-full flex items-center space-x-4 p-4 bg-secondary-50 hover:bg-secondary-100 rounded-lg transition-colors duration-150"
                  >
                    <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center`}>
                      <Icon name={action.icon} size={20} className="text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="font-medium text-text-primary">{action.title}</h4>
                      <p className="text-sm text-text-secondary">{action.description}</p>
                    </div>
                    {action.count !== null && (
                      <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-sm font-medium">
                        {action.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Performance Analytics */}
            <div className="card">
              <div className="p-6 border-b border-secondary-200">
                <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
                  <Icon name="BarChart3" size={20} className="text-accent" />
                  <span>Performance Analytics</span>
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Approval Rate</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-secondary-200 rounded-full h-2">
                      <div 
                        className="bg-success h-2 rounded-full" 
                        style={{ width: `${dashboardData.analytics.approval_rate}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-success">
                      {dashboardData.analytics.approval_rate}%
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Selection Rate</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-secondary-200 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full" 
                        style={{ width: `${dashboardData.analytics.selection_rate * 10}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-primary">
                      {dashboardData.analytics.selection_rate}%
                    </span>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-secondary-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-text-secondary">Avg. Processing Time</span>
                    <span className="text-sm font-medium text-text-primary">
                      {dashboardData.analytics.avg_processing_time} days
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RecruiterDashboard;
